import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { isPlainLeftClick } from '../../../../utils/isPlainLeftClick';
import type { AiChatSource } from '../../types/aiChat.types';

type Props = {
  sources: AiChatSource[];
};

type SourceLink = {
  source: AiChatSource;
  href: string;
  isExternal: boolean;
};

export function getSourcePath(source: AiChatSource): string | null {
  switch (source.type) {
    case 'subject':
      return `/topics/${source.id}`;
    case 'provider':
      return `/providers/${source.id}`;
    case 'meeting':
      return '/council/meetings';
    case 'event':
    case 'reminder':
      return '/calendar';
    default:
      return null;
  }
}

export function useChatSources(props: Props) {
  const spacePath = useSpacePath();
  const navigate = useNavigate();

  const links = props.sources.flatMap((source): SourceLink[] => {
    if (source.type === 'document') {
      return source.url ? [{ source, href: source.url, isExternal: true }] : [];
    }
    const path = getSourcePath(source);
    return path ? [{ source, href: spacePath(path), isExternal: false }] : [];
  });

  function handleInternalClick(event: MouseEvent<HTMLElement>, href: string) {
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    navigate(href);
  }

  return {
    links,
    handleInternalClick,
  };
}
